// ────────────────────────────────────────────────────────────────────────────
// The order's status actions, as three pieces the page can place separately.
//
// WHY A HOOK RETURNING NODES: the two controls sit in different parts of the
// page — advancing in the header beside the status, cancelling in the footer
// where the delete control was — but they share one confirmation dialog and one
// "which move is running" flag. Passing that state down two branches of the
// tree would mean prop-drilling it through the header grid and the footer, or
// rendering two dialogs and hoping only one ever opens.
//
// THREE OUTCOMES, DELIBERATELY DISTINCT:
//   • clean — the control moves the order, no ceremony
//   • warn  — it still moves, after a dialog listing what is imperfect. A
//             warning that behaves like an error teaches people to click
//             through both without reading.
//   • stop  — the control is disabled and its tooltip says what is missing.
//             Disabled rather than absent: "why can't I open this order?"
//             deserves an answer.
//
// The reasons come from the SAME evaluator the settings screen previews with,
// so what an operator configured is what they get here.
// ────────────────────────────────────────────────────────────────────────────
import { ConfirmDialog } from '@my/ui';
import { HelpCircle, Undo2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { requirementMeta } from '@/features/settings/workflows/lib/mockWorkflow';
import type { Unmet } from '@/features/settings/workflows/lib/evaluate';
import {
  OrderAdvanceButton,
  OrderCancelButton,
  UnmetLine,
} from '../components/OrderMoveButtons';
import { RowActionsMenu } from '@/components/RowActionsMenu';
import type { MoveAssessment } from './orderWorkflow';

export interface OrderActions {
  /** The forward move control, or null on a terminal state. */
  advance: ReactNode;
  /**
   * The "⋮" carrying the CORRECTIONS — moving the order back to an earlier
   * status. Null when there is nowhere back to go (a draft).
   */
  corrections: ReactNode;
  /** The cancel control, or null where cancelling is not a move on offer. */
  cancel: ReactNode;
  /** Render once, anywhere — the shared confirmation. */
  dialog: ReactNode;
}

export function useOrderActions(args: {
  /** The forward move and the cancellation, already assessed. */
  moves: MoveAssessment[];
  /**
   * Moves back to an earlier status. They carry no requirements — the workflow
   * says what must be true to go FORWARD, and nothing describes what must be
   * true to admit a mistake — so each one asks before it runs and the audit
   * trail is what keeps it honest.
   */
  corrections?: MoveAssessment[];
  /** Applies a move. Resolves when the order has been saved. */
  onMove: (move: MoveAssessment) => Promise<void> | void;
  /** A mutation is in flight. */
  busy?: boolean;
  /** Why every control is unavailable, e.g. an invalid temperature. */
  disabledReason?: string;
}): OrderActions {
  const { moves, corrections = [], onMove, busy, disabledReason } = args;

  /** The move awaiting confirmation — warnings, or any backwards move. */
  const [confirming, setConfirming] = useState<MoveAssessment | null>(null);
  /** True while the pending confirmation is a correction rather than progress. */
  const [reverting, setReverting] = useState(false);
  const [running, setRunning] = useState<string | null>(null);

  const run = async (move: MoveAssessment) => {
    setConfirming(null);
    setRunning(move.toStatus);
    try {
      await onMove(move);
    } finally {
      setRunning(null);
    }
  };

  const attempt = (move: MoveAssessment) => {
    if (move.blocked) return;
    setReverting(false);
    // Warnings and unverifiable checks both get the dialog: the second is not a
    // failure, but an operator should know a rule could not be checked before
    // they rely on it having been.
    if (move.unmet.length > 0 || move.unverifiable.length > 0) {
      setConfirming(move);
      return;
    }
    void run(move);
  };

  const revert = (move: MoveAssessment) => {
    setReverting(true);
    setConfirming(move);
  };

  const cancelling = moves.find((m) => m.toStatus === 'Cancelled');
  const forward = moves.find((m) => m.toStatus !== 'Cancelled');

  return {
    advance: forward ? (
      <OrderAdvanceButton
        move={forward}
        busy={busy || running === forward.toStatus}
        disabledReason={disabledReason}
        onClick={() => attempt(forward)}
      />
    ) : null,

    corrections:
      corrections.length > 0 ? (
        <RowActionsMenu
          label="Order status"
          actions={corrections.map((move) => ({
            id: move.toStatus,
            label: `Move back to ${move.toLabel}`,
            icon: <Undo2 className="h-3.5 w-3.5 text-slate-500" />,
            onSelect: () => revert(move),
            disabledReason: busy || running !== null ? 'Saving…' : disabledReason,
          }))}
        />
      ) : null,

    cancel: cancelling ? (
      <OrderCancelButton
        move={cancelling}
        busy={busy || running === cancelling.toStatus}
        disabledReason={disabledReason}
        onClick={() => attempt(cancelling)}
      />
    ) : null,

    dialog: (
      <ConfirmDialog
        open={!!confirming}
        title={confirming ? `Move to ${confirming.toLabel}?` : ''}
        message={
          confirming ? (
            <div className="space-y-2">
              {reverting && (
                <p className="text-xs text-slate-600">
                  This moves the order BACK to {confirming.toLabel}. Nothing that has already
                  happened is undone — invoices, documents and recorded times stay exactly as
                  they are — and the change is recorded in the order&apos;s history.
                </p>
              )}
              {confirming.unmet.length > 0 && (
                <>
                  <p className="text-xs text-slate-600">
                    This order does not meet everything configured for the move. Nothing here
                    blocks it — you can go ahead.
                  </p>
                  <ul className="space-y-1">
                    {confirming.unmet.map((u) => (
                      <UnmetLine key={`${u.kind}-${u.severity}`} unmet={u} />
                    ))}
                  </ul>
                </>
              )}
              {confirming.unverifiable.length > 0 && (
                <p className="flex items-start gap-2 rounded-sm bg-slate-50 p-2 text-xs text-slate-600">
                  <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span>
                    {confirming.unverifiable
                      .map((kind) => requirementMeta(kind as Unmet['kind']).label)
                      .join(', ')}{' '}
                    could not be checked — nothing records it yet, so this rule has not been
                    applied either way.
                  </span>
                </p>
              )}
            </div>
          ) : (
            ''
          )
        }
        confirmLabel={confirming ? `Move to ${confirming.toLabel}` : 'Move'}
        onConfirm={() => confirming && void run(confirming)}
        onCancel={() => setConfirming(null)}
      />
    ),
  };
}
